import {
  removeBasketItems,
  addWishlistItemBySKU,
  removeWishlistItemBySKU,
  changeQuantity,
} from "lib/api"
import type { ItemType } from "types"
import type { ProductListVariants } from "components/BasketLists/ProductList/ProductList"
import { useExpressCheckoutContext } from "components/Basket/hooks/useExpressCheckoutContext"
import { useATBConfirmationContext } from "basket_and_checkout/shared/components/ATBConfirmation/context/ATBConfirmationContext"

export const locationTypes = {
  basket: "BASKET_CROSSSELL_CAROUSEL",
  atbConfirmation: "ATB_CONFIRMATION",
  default: "Page",
}

type OnAddRemoveWishlist = (
  item: ItemType,
  isWishListed: boolean
) => Promise<void>

export const useAddRemoveWishlist = (): {
  onAddRemoveWishlist: OnAddRemoveWishlist
} => {
  const onAddRemoveWishlist = async (item: ItemType, isWishListed: boolean) => {
    if (!item.variant) return

    try {
      if (isWishListed) {
        await removeWishlistItemBySKU(item.variant.sku)
      } else {
        await addWishlistItemBySKU(item.variant.sku)
      }
    } catch (err) {
      console.error(err)
    }
  }

  return { onAddRemoveWishlist }
}

type OnRemoveBasketItem = (item: ItemType) => Promise<void>

export const useOnRemoveBasketItem = (
  cardVariant?: ProductListVariants
): {
  onRemoveBasketItem: OnRemoveBasketItem
} => {
  const { handleExpressCheckout } = useExpressCheckoutContext()

  const onRemoveBasketItem = async (item: ItemType) => {
    try {
      await removeBasketItems([item], locationTypes[cardVariant ?? "default"])

      if (handleExpressCheckout) {
        handleExpressCheckout()
      }
    } catch (err) {
      console.error(err)
    }
  }

  return { onRemoveBasketItem }
}

type OnChangeQuantity = (item: ItemType, value: number) => Promise<void>

export const useOnChangeQuantity = (
  cardVariant?: ProductListVariants
): {
  onChangeQuantity: OnChangeQuantity
} => {
  const { setIsATBLoading } = useATBConfirmationContext()
  const { handleExpressCheckout } = useExpressCheckoutContext()

  const disableGlobalLoader = cardVariant === "atbConfirmation"

  const onChangeQuantity = async (item: ItemType, value: number) => {
    try {
      if (disableGlobalLoader) {
        setIsATBLoading(true)
      }
      await changeQuantity(
        item.id,
        value,
        locationTypes[cardVariant ?? "default"],
        {
          runInBackground: disableGlobalLoader,
        }
      )
      if (handleExpressCheckout) {
        handleExpressCheckout()
      }
    } catch (err) {
      console.error(err)
    } finally {
      setIsATBLoading(false)
    }
  }

  return { onChangeQuantity }
}
